import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone, MessageCircle, Home, Building2, Hammer, Zap, Package, Type, Wrench, Grid3x3 } from 'lucide-react';
import { getServices } from '../data/api';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { Button } from './ui/button';

const iconMap = {
  Home, Building2, Hammer, Lightbulb: Zap, Package, Zap, Type, Wrench, Grid3x3
};

const serviceColors = {
  '1': { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  '2': { bg: 'bg-indigo-600', light: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  '3': { bg: 'bg-amber-600', light: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  '4': { bg: 'bg-orange-600', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  '5': { bg: 'bg-pink-600', light: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-200' },
  '6': { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  '7': { bg: 'bg-cyan-600', light: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
  '8': { bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  '9': { bg: 'bg-rose-600', light: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
};

const ServiceDetailPage = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openChat, getServiceMessage } = useWhatsApp();

  useEffect(() => {
    setLoading(true);
    getServices()
      .then(data => {
        setAllServices(data);
        const found = data.find(s => s.id === id);
        setService(found || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center" data-testid="service-not-found">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/#services" className="text-blue-600 hover:underline">Back to Services</Link>
        </div>
      </div>
    );
  }

  const colors = serviceColors[service.id] || serviceColors['1'];
  const IconComponent = iconMap[service.icon] || Home;
  const otherServices = allServices.filter(s => s.id !== id).slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="service-detail-page">
      {/* Hero */}
      <header className={`${colors.bg} text-white py-16 lg:py-24`}>
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/#services" className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors" data-testid="service-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Services
          </Link>
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" data-testid="service-detail-title">
                {service.title}
              </h1>
              {service.priceRange && (
                <p className="text-white/80 text-lg">Starting from {service.priceRange}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Service</h2>
              <p className="text-gray-600 leading-relaxed text-base">
                {service.description}
              </p>
            </div>

            {/* Features */}
            {service.features && service.features.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">What's Included</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {service.features.map((feature) => (
                    <div key={feature} className={`flex items-center gap-3 p-3 rounded-lg ${colors.light} ${colors.border} border`}>
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${colors.text}`} />
                      <span className="text-sm text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Process */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Our Process</h2>
              <div className="space-y-4">
                {['Consultation & Site Visit', 'Design Concept & Planning', 'Quotation & Agreement', 'Construction & Execution', 'Quality Check & Handover'].map((step, idx) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full ${colors.bg} text-white flex items-center justify-center font-bold text-sm flex-shrink-0`}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* CTA Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border-t-4 border-blue-600 sticky top-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Get a Free Quote</h3>
              <p className="text-sm text-gray-600 mb-4">
                Interested in our {service.title} service? Contact us for a no-obligation consultation.
              </p>
              <div className="space-y-3">
                <Button
                  className={`${colors.bg} hover:opacity-90 text-white w-full`}
                  onClick={() => {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                    else window.location.href = '/#contact';
                  }}
                  data-testid="service-detail-quote-btn"
                >
                  <Phone className="w-4 h-4 mr-2" /> Request Quote
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openChat({
                    page: 'service-detail',
                    context: service.title,
                    customMessage: getServiceMessage(service.title),
                  })}
                  data-testid="service-detail-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
                </Button>
              </div>
              <div className="mt-4 pt-4 border-t text-sm text-gray-500 space-y-1">
                <p>Phone: +65 6747-7740</p>
                <p>Email: yusendes@singnet.com.sg</p>
              </div>
            </div>

            {/* Other Services */}
            {otherServices.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Other Services</h3>
                <div className="space-y-2">
                  {otherServices.map(s => {
                    const SIcon = iconMap[s.icon] || Home;
                    return (
                      <Link
                        key={s.id}
                        to={`/services/${s.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                        data-testid={`other-service-${s.id}`}
                      >
                        <SIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                        <span className="text-sm text-gray-700 group-hover:text-blue-600 font-medium">{s.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p>&copy; 2025 Yusen Design & Contract Pte Ltd. All rights reserved.</p>
          <p className="text-sm mt-2">Interior Design & Renovation Contractor Singapore | BCA Registered</p>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetailPage;
